import { Card } from '../ui/Card';
import { FileText, Download, Printer, AlertTriangle, CheckCircle, Calendar, DollarSign, FileCheck, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { DECLARATION_TYPES, DECLARATION_STATUS_LABELS, PERIODICITY_LABELS } from '../../types/declarations';
import type { Declaration } from '../../types/declarations';
import { useCurrency } from '../../hooks/useCurrency';

interface DeclarationPreviewProps {
  declaration: Declaration;
  onDownload: () => void;
  onPrint: () => void;
}

export function DeclarationPreview({ declaration, onDownload, onPrint }: DeclarationPreviewProps) {
  const { format } = useCurrency();
  const declarationType = DECLARATION_TYPES[declaration.type];
  
  const getStatusIcon = (status: Declaration['status']) => {
    switch (status) {
      case 'validated': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected': return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'submitted': return <FileCheck className="h-5 w-5 text-blue-600" />;
      case 'pending': return <Clock className="h-5 w-5 text-yellow-600" />;
      default: return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: Declaration['status']) => {
    switch (status) {
      case 'validated': return 'bg-green-50 border-green-200 text-green-800';
      case 'rejected': return 'bg-red-50 border-red-200 text-red-800';
      case 'submitted': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'pending': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getTotalAmount = () => {
    return declaration.amount + (declaration.additionalFees || 0) + (declaration.penalties || 0);
  };

  const formatPeriod = () => {
    const date = new Date(declaration.period);
    if (declarationType.periodicity === 'annual') {
      return date.getFullYear().toString();
    } else if (declarationType.periodicity === 'quarterly') {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    } else {
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec statut */}
      <div className={`p-4 rounded-lg border-2 ${getStatusColor(declaration.status)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(declaration.status)}
            <div>
              <h3 className="font-semibold text-lg">{declarationType.label}</h3>
              <p className="text-sm opacity-75">
                Référence: {declaration.reference} • {DECLARATION_STATUS_LABELS[declaration.status]}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-75">Échéance</p>
            <p className="font-medium">
              {new Date(declaration.dueDate).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
      </div>

      {/* Informations générales */}
      <Card title="Informations de la déclaration" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Période</p>
              <p className="font-medium">{formatPeriod()}</p>
              <p className="text-xs text-gray-400">{PERIODICITY_LABELS[declarationType.periodicity]}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Taux d'imposition</p>
              <p className="font-medium">{declarationType.rate}</p>
              <p className="text-xs text-gray-400">{declarationType.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <FileCheck className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-500">Catégorie</p>
              <p className="font-medium">
                {declarationType.category === 'direct_tax' ? 'Impôt Direct' :
                 declarationType.category === 'indirect_tax' ? 'Taxe Indirecte' :
                 declarationType.category === 'social_contribution' ? 'Cotisation Sociale' :
                 'Taxe Spécifique'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Montants détaillés */}
      <Card title="Détail des montants" icon={DollarSign}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Montant de base</p>
              <p className="text-xl font-semibold text-blue-800">
                {format(declaration.amount, 'CDF')}
              </p>
            </div>

            {(declaration.additionalFees || 0) > 0 && (
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 font-medium">Frais supplémentaires</p>
                <p className="text-xl font-semibold text-orange-800">
                  {format(declaration.additionalFees || 0, 'CDF')}
                </p>
                <p className="text-xs text-orange-600">Non justifiés</p>
              </div>
            )}

            {(declaration.penalties || 0) > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Pénalités</p>
                <p className="text-xl font-semibold text-red-800">
                  {format(declaration.penalties || 0, 'CDF')}
                </p>
                <p className="text-xs text-red-600">Retard de paiement</p>
              </div>
            )}
          </div>

          {((declaration.additionalFees || 0) > 0 || (declaration.penalties || 0) > 0) && (
            <div className="border-t pt-4">
              <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
                <span className="font-medium text-gray-700">Total à payer</span>
                <span className="text-2xl font-bold text-gray-900">
                  {format(getTotalAmount(), 'CDF')}
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Documents joints */}
      <Card title="Documents et pièces jointes" icon={FileText}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className={`h-5 w-5 ${declaration.justificationDocument ? 'text-green-600' : 'text-red-600'}`} />
                <div>
                  <p className="font-medium">Justificatif de paiement</p>
                  <p className="text-sm text-gray-500">
                    {declaration.justificationDocument ? 'Document disponible' : 'Document requis'}
                  </p>
                </div>
              </div>
              {declaration.justificationDocument && (
                <Button variant="secondary" size="sm" icon={Download}>
                  Télécharger
                </Button>
              )}
            </div>

            {declaration.declarationForm && (
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Formulaire de déclaration</p>
                    <p className="text-sm text-gray-500">Document rempli</p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" icon={Download}>
                  Télécharger
                </Button>
              </div>
            )}
          </div>

          {declaration.attachments && declaration.attachments.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Autres pièces jointes</h4>
              <div className="space-y-2">
                {declaration.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      <span className="text-sm">{attachment.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(attachment.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button variant="secondary" size="sm" icon={Download}>
                      Télécharger
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notes et observations */}
      {declaration.notes && (
        <Card title="Notes et observations">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{declaration.notes}</p>
          </div>
        </Card>
      )}

      {/* Historique des actions */}
      <Card title="Historique">
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span className="text-gray-500">Créé le</span>
            <span className="font-medium">
              {declaration.submittedAt ? new Date(declaration.submittedAt).toLocaleString('fr-FR') : 'N/A'}
            </span>
          </div>

          {declaration.submittedAt && (
            <div className="flex items-center gap-3 text-sm">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              <span className="text-gray-500">Soumis le</span>
              <span className="font-medium">
                {new Date(declaration.submittedAt).toLocaleString('fr-FR')}
              </span>
              {declaration.submittedBy && (
                <span className="text-gray-500">par {declaration.submittedBy}</span>
              )}
            </div>
          )}

          {declaration.validatedAt && (
            <div className="flex items-center gap-3 text-sm">
              <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
              <span className="text-gray-500">Validé le</span>
              <span className="font-medium">
                {new Date(declaration.validatedAt).toLocaleString('fr-FR')}
              </span>
              {declaration.validatedBy && (
                <span className="text-gray-500">par {declaration.validatedBy}</span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          variant="secondary"
          icon={Printer}
          onClick={onPrint}
        >
          Imprimer
        </Button>
        <Button
          variant="primary"
          icon={Download}
          onClick={onDownload}
        >
          Télécharger PDF
        </Button>
      </div>
    </div>
  );
}